<template>
  <section class="news-article-page page-stack">
    <article class="card page-hero-card news-hero">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Wowhead Article</p>
          <h2>{{ article.title || 'Loading article...' }}</h2>
          <!-- <p>{{ article.description || 'Reading the source article from Wowhead.' }}</p> -->
        </div>
      </header>
    </article>

    <div class="card news-article-actions">
      <RouterLink class="btn-outline" to="/news">
        <i class="fa-solid fa-arrow-left"></i>
        News
      </RouterLink>
      <div class="language-dropdown" :class="{ 'is-open': languageMenuOpen }" aria-label="News language">
        <button
          class="btn-sm-outline language-dropdown-trigger"
          type="button"
          :disabled="articleTranslating"
          aria-haspopup="menu"
          :aria-expanded="languageMenuOpen"
          @click.stop="languageMenuOpen = !languageMenuOpen"
        >
          <i class="fa-solid fa-language"></i>
          {{ activeLanguageLabel }}
          <i class="fa-solid fa-chevron-down"></i>
        </button>
        <Transition name="favorite-menu">
          <div v-if="languageMenuOpen" class="language-dropdown-menu" role="menu" @click.stop>
            <button
              v-for="language in languages"
              :key="language.value"
              type="button"
              role="menuitem"
              :class="{ 'is-active': activeLanguage === language.value }"
              @click="selectLanguage(language.value)"
            >
              <span>{{ language.name }}</span>
              <i v-if="activeLanguage === language.value" class="fa-solid fa-check"></i>
            </button>
          </div>
        </Transition>
      </div>
      <span class="translation-provider-chip">
        <i class="fa-solid fa-bolt"></i>
        {{ translationProviderLabel }}
      </span>
      <a v-if="article.url" class="btn-outline" :href="article.url" target="_blank" rel="noreferrer">
        <i class="fa-solid fa-arrow-up-right-from-square"></i>
        Source
      </a>
    </div>

    <div class="news-article-layout">
      <main class="news-article-main">
        <article v-if="loading" class="card news-article-shell">
          <span class="skeleton-media news-article-hero-skeleton"></span>
          <span class="skeleton-line skeleton-line-medium"></span>
          <span class="skeleton-line"></span>
          <span class="skeleton-line"></span>
          <span class="skeleton-line skeleton-line-short"></span>
        </article>

        <AppEmptyState
          v-else-if="error"
          eyebrow="Article"
          icon="fa-solid fa-triangle-exclamation"
          title="Unable to load the article"
          :message="error"
          :actions="[{ to: '/news', label: 'Back to News', icon: 'fa-solid fa-arrow-left' }]"
        />

        <article v-else class="card news-article-shell">
          <img v-if="article.imageUrl" class="news-article-banner" :src="article.imageUrl" :alt="article.title" loading="lazy">

          <header class="news-article-meta">
            <span v-if="article.author">{{ article.author }}</span>
            <time v-if="article.publishedAt" :datetime="article.publishedAt">{{ formatDate(article.publishedAt) }}</time>
          </header>

          <section class="news-article-body">
            <template v-for="(block, index) in article.blocks" :key="`${block.type}-${index}`">
              <h2 v-if="block.type === 'heading' && block.level === 2">{{ block.text }}</h2>
              <h3 v-else-if="block.type === 'heading'">{{ block.text }}</h3>
              <p v-else-if="block.type === 'paragraph'">{{ block.text }}</p>
              <div v-else-if="block.type === 'html' && block.tag === 'table'" class="news-article-table-block" v-html="block.html"></div>
              <p v-else-if="block.type === 'html' && block.tag === 'p'" v-html="block.html"></p>
              <blockquote v-else-if="block.type === 'quote'" v-html="block.html || block.text"></blockquote>
              <aside v-else-if="block.type === 'bluepost'" class="news-bluepost">
                <header>
                  <span><i class="fa-brands fa-battle-net"></i> {{ block.title || 'Blizzard' }}</span>
                  <a v-if="block.url" :href="block.url" target="_blank" rel="noreferrer">
                    Source
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                  </a>
                </header>
                <div class="news-bluepost-content" v-html="block.html"></div>
              </aside>
              <div v-else-if="block.type === 'image' && block.placeholder" class="news-article-media-placeholder">
                <i class="fa-solid fa-image"></i>
                <span>{{ block.alt || 'Image unavailable' }}</span>
              </div>
              <img v-else-if="block.type === 'image'" :src="block.src" :alt="block.alt || ''" loading="lazy">
              <video v-else-if="block.type === 'video'" :src="block.src" controls preload="metadata"></video>
              <div v-else-if="block.type === 'embed' && block.placeholder" class="news-article-media-placeholder">
                <i class="fa-brands fa-youtube"></i>
                <span>{{ block.title || 'Video unavailable' }}</span>
              </div>
              <iframe
                v-else-if="block.type === 'embed'"
                :src="block.src"
                :title="block.title"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>
              <ol v-else-if="block.type === 'list' && block.ordered">
                <li v-for="item in block.items" :key="item" v-html="item"></li>
              </ol>
              <ul v-else-if="block.type === 'list'">
                <li v-for="item in block.items" :key="item" v-html="item"></li>
              </ul>
            </template>
          </section>
        </article>
      </main>

      <aside class="card news-other-posts">
        <header>
          <p class="eyebrow">Latest</p>
          <h3>Other posts</h3>
        </header>
        <div v-if="otherPostsLoading" class="news-other-posts-list">
          <span v-for="index in 5" :key="index" class="skeleton-line"></span>
        </div>
        <div v-else class="news-other-posts-list">
          <RouterLink v-for="post in otherPosts" :key="post.id" :to="articleRoute(post)">
            <span>{{ post.category }}</span>
            <strong>{{ post.title }}</strong>
            <time v-if="post.publishedAt" :datetime="post.publishedAt">{{ formatDate(post.publishedAt) }}</time>
          </RouterLink>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import { getWowheadArticle, getWowheadFeedPosts } from '../services/wowheadNews'
import {
  getLastTranslationProvider,
  mapWithConcurrency,
  startTranslationCollection,
  stopTranslationCollection,
  summarizeTranslationProviders,
  setTranslationProvider,
  translateText,
} from '../services/translation'

const NEWS_LANGUAGE_KEY = 'rio-news-language'

function loadNewsLanguage() {
  try {
    const language = localStorage.getItem(NEWS_LANGUAGE_KEY)
    return ['en', 'uk', 'ru'].includes(language) ? language : 'en'
  } catch {
    return 'en'
  }
}

export default {
  name: 'NewsArticleView',
  components: {
    AppEmptyState,
  },
  data() {
    return {
      article: {
        title: '',
        description: '',
        imageUrl: '',
        author: '',
        publishedAt: '',
        url: '',
        blocks: [],
      },
      loading: false,
      error: '',
      otherPosts: [],
      otherPostsLoading: false,
      sourceArticle: null,
      articleTranslating: false,
      activeLanguage: loadNewsLanguage(),
      languageMenuOpen: false,
      translationProvider: getLastTranslationProvider(),
      languages: [
        { value: 'en', label: 'EN', name: 'English' },
        { value: 'uk', label: 'UA', name: 'Українська' },
        { value: 'ru', label: 'RU', name: 'Русский' },
      ],
    }
  },
  computed: {
    articleUrl() {
      return String(this.$route.query.url || '')
    },
    activeLanguageLabel() {
      return this.languages.find((language) => language.value === this.activeLanguage)?.label || 'EN'
    },
    translationProviderLabel() {
      if (this.activeLanguage === 'en') return 'No translate'
      if (this.articleTranslating) return 'In process...'
      return this.formatTranslationProvider(this.translationProvider)
    },
  },
  watch: {
    articleUrl: {
      immediate: true,
      handler() {
        this.loadArticle()
        this.loadOtherPosts()
      },
    },
    activeLanguage() {
      this.persistNewsLanguage()
      this.applyArticleLanguage()
    },
  },
  mounted() {
    document.addEventListener('click', this.closeLanguageMenu)
    window.addEventListener('rio:translation-provider', this.updateTranslationProvider)
    this.loadWowheadTooltips()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeLanguageMenu)
    window.removeEventListener('rio:translation-provider', this.updateTranslationProvider)
  },
  methods: {
    updateTranslationProvider(event) {
      this.translationProvider = event.detail || null
    },
    formatTranslationProvider(provider) {
      if (provider?.status === 'limit') return 'Limit reached'

      const label = provider?.label || 'Unknown provider'
      const queue = provider?.index && provider?.total ? ` - ${provider.index}/${provider.total}` : ''
      const cache = provider?.cached ? 'Cache - ' : ''
      return `${cache}${label}${queue}`
    },
    finalizeTranslationProvider() {
      if (this.activeLanguage === 'en') return

      const provider = getLastTranslationProvider()
      this.translationProvider = provider?.status && provider.status !== 'none'
        ? provider
        : {
          key: 'article-cache',
          label: 'Article cache',
          cached: true,
          status: 'cached',
        }
    },
    closeLanguageMenu() {
      this.languageMenuOpen = false
    },
    selectLanguage(language) {
      this.activeLanguage = language
      this.languageMenuOpen = false
    },
    async loadArticle() {
      if (!this.articleUrl) {
        this.error = 'Missing Wowhead article URL.'
        return
      }

      this.loading = true
      this.error = ''
      const loadingId = await this.$store.dispatch('startGlobalLoading', 'Loading Wowhead article...')

      try {
        this.sourceArticle = await getWowheadArticle(this.articleUrl)
        await this.applyArticleLanguage()
      } catch (error) {
        this.error = 'Wowhead article is unavailable right now. Try opening the source link directly.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Article unavailable',
          description: this.error,
        })
      } finally {
        this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
        this.prepareWowheadArticleTooltips()
      }
    },
    async loadOtherPosts() {
      this.otherPostsLoading = true

      try {
        const currentUrl = this.articleUrl
        const posts = await getWowheadFeedPosts('all', 'en')
        this.otherPosts = posts
          .filter((post) => post.link && post.link !== currentUrl)
          .slice(0, 5)
      } catch {
        this.otherPosts = []
      } finally {
        this.otherPostsLoading = false
      }
    },
    async applyArticleLanguage() {
      if (!this.sourceArticle) return

      if (this.activeLanguage === 'en') {
        this.article = this.sourceArticle
        if (!this.loading) this.prepareWowheadArticleTooltips()
        return
      }

      this.articleTranslating = true
      const translationLoadingId = await this.$store.dispatch('startGlobalLoading', `Translating article to ${this.activeLanguageLabel}...`)

      try {
        this.article = await this.translateArticle(this.sourceArticle, this.activeLanguage)
        if (!this.loading) this.prepareWowheadArticleTooltips()
      } finally {
        this.finalizeTranslationProvider()
        this.$store.dispatch('finishGlobalLoading', translationLoadingId)
        this.articleTranslating = false
      }
    },
    async translateArticle(article, targetLang) {
      const collector = startTranslationCollection()

      try {
        const [title, description, blocks] = await Promise.all([
          translateText(article.title, targetLang),
          translateText(article.description, targetLang),
          mapWithConcurrency(article.blocks || [], 3, (block) => this.translateArticleBlock(block, targetLang)),
        ])

        setTranslationProvider(summarizeTranslationProviders(collector))

        return {
          ...article,
          title,
          description,
          blocks,
          originalTitle: article.title,
          originalDescription: article.description,
        }
      } finally {
        stopTranslationCollection(collector)
      }
    },
    async translateArticleBlock(block, targetLang) {
      if (block.type === 'heading') {
        return { ...block, text: await translateText(block.text, targetLang), originalText: block.text }
      }

      if (block.type === 'paragraph') {
        return { ...block, text: await translateText(block.text, targetLang), originalText: block.text }
      }

      if (block.type === 'html') {
        return { ...block, html: await this.translateHtml(block.html, targetLang), originalHtml: block.html }
      }

      if (block.type === 'quote' || block.type === 'bluepost') {
        return { ...block, html: await this.translateHtml(block.html || block.text || '', targetLang), originalHtml: block.html }
      }

      if (block.type === 'list') {
        const items = await mapWithConcurrency(block.items || [], 3, (item) => this.translateHtml(item, targetLang))
        return { ...block, items, originalItems: block.items }
      }

      return block
    },
    async translateHtml(html, targetLang) {
      if (!html) return ''

      const template = document.createElement('template')
      template.innerHTML = html
      const textNodes = []
      const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)
      let node = walker.nextNode()

      while (node) {
        if (node.textContent.trim()) textNodes.push(node)
        node = walker.nextNode()
      }

      await mapWithConcurrency(textNodes, 3, async (textNode) => {
        textNode.textContent = await translateText(textNode.textContent, targetLang)
      })

      this.normalizeInlineLinkSpacing(template.content)
      return template.innerHTML
    },
    normalizeInlineLinkSpacing(root) {
      root.querySelectorAll('a').forEach((link) => {
        const previous = link.previousSibling
        const next = link.nextSibling

        if (previous?.nodeType === Node.TEXT_NODE && previous.textContent && !/\s$/.test(previous.textContent)) {
          previous.textContent += ' '
        }

        if (next?.nodeType === Node.TEXT_NODE && next.textContent && !/^\s/.test(next.textContent)) {
          next.textContent = ` ${next.textContent}`
        }
      })
    },
    persistNewsLanguage() {
      try {
        localStorage.setItem(NEWS_LANGUAGE_KEY, this.activeLanguage)
      } catch {
        // Ignore storage failures; language falls back to English.
      }
    },
    articleRoute(post) {
      return {
        path: '/news/article',
        query: { url: post.link },
      }
    },
    prepareWowheadArticleTooltips() {
      this.$nextTick(() => {
        this.$el.querySelectorAll('.news-article-body a[href*="wowhead.com/"]').forEach((link) => {
          const tooltipData = this.wowheadTooltipData(link.href)
          if (!tooltipData) return

          link.dataset.wowhead = tooltipData
          link.target = '_blank'
          link.rel = 'noreferrer'
        })

        this.loadWowheadTooltips()
      })
    },
    wowheadTooltipData(value) {
      try {
        const url = new URL(value)
        const match = url.pathname.match(/\/(item|spell|quest|npc|currency|event|achievement|affix|object)=(\d+)/i)
        return match ? `${match[1].toLowerCase()}=${match[2]}` : ''
      } catch {
        return ''
      }
    },
    loadWowheadTooltips() {
      if (typeof window === 'undefined') return

      window.whTooltips = {
        colorLinks: false,
        iconizeLinks: false,
        renameLinks: false,
        ...(window.whTooltips || {}),
      }

      if (document.querySelector('script[data-wowhead-tooltips]')) {
        this.refreshWowheadTooltipsWithRetry()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://wow.zamimg.com/js/tooltips.js'
      script.async = true
      script.dataset.wowheadTooltips = 'true'
      script.addEventListener('load', () => this.refreshWowheadTooltipsWithRetry())
      document.head.appendChild(script)
    },
    refreshWowheadTooltipsWithRetry(attempt = 0) {
      this.$nextTick(() => {
        if (window.$WowheadPower?.refreshLinks) {
          window.$WowheadPower.refreshLinks()
          return
        }

        if (attempt < 6) {
          window.setTimeout(() => this.refreshWowheadTooltipsWithRetry(attempt + 1), 250)
        }
      })
    },
    formatDate(value) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
  },
}
</script>
